import { Component, type ErrorInfo, type ReactNode } from 'react';

import { i18n } from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Root render guard. Before this existed, ANY uncaught exception thrown
 * during render — e.g. `localStorage` access throwing `SecurityError` in
 * an iOS WKWebView with storage blocked — unmounted the whole React tree
 * and left the user staring at a blank white page with no way out.
 *
 * A class component is the only way to catch render errors in React. The
 * fallback is deliberately self-contained (inline styles, no app hooks or
 * context, no network) so it renders even when the failure is deep in the
 * providers below `main.tsx`. Copy falls back to English if the i18n
 * singleton itself is the thing that broke.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface it in the console for anyone who does open devtools; there
    // is no client-side error sink to report to.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private copy(): { title: string; body: string; reload: string } {
    const ru = i18n?.language?.startsWith('ru');
    if (ru) {
      return {
        title: 'Что-то пошло не так',
        body: 'Не удалось загрузить приложение. Попробуйте перезагрузить.',
        reload: 'Перезагрузить',
      };
    }
    return {
      title: 'Something went wrong',
      body: "The app couldn't load. Please try reloading.",
      reload: 'Reload',
    };
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const c = this.copy();
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '0 24px',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          color: '#e9edf2',
          background: '#0b0f14',
        }}
      >
        <span style={{ fontSize: 22, fontWeight: 600 }}>{c.title}</span>
        <span style={{ fontSize: 14, opacity: 0.7, maxWidth: 320 }}>
          {c.body}
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: 8,
            padding: '10px 20px',
            borderRadius: 12,
            border: 'none',
            fontSize: 15,
            fontWeight: 600,
            color: '#0b0f14',
            background: '#4da3ff',
            cursor: 'pointer',
          }}
        >
          {c.reload}
        </button>
      </div>
    );
  }
}
